import React from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "sonner";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingButtons from "./components/FloatingButtons";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import HomePage from "./pages/HomePage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import NotFoundPage from "./pages/NotFoundPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminContent from "./pages/admin/AdminContent";
import AdminProcessSteps from "./pages/admin/AdminProcessSteps";
import AdminOrders from "./pages/admin/AdminOrders";

// Order Journey Pages
import StartPage from "./pages/order/StartPage";
import ScheduleVisitPage from "./pages/order/ScheduleVisitPage";
import ShowroomPage from "./pages/order/ShowroomPage";
import AgreementPage from "./pages/order/AgreementPage";
import ProductionPage from "./pages/order/ProductionPage";
import InstallationPage from "./pages/order/InstallationPage";
import ClientDashboardPage from "./pages/order/ClientDashboardPage";
import FollowUpPage from "./pages/FollowUpPage";

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      {children}
      <Footer />
      <FloatingButtons />
    </>
  );
};

// Route wrapper to conditionally apply layout
const AppRoutes = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="process-steps" element={<AdminProcessSteps />} />
            <Route path="orders" element={<AdminOrders />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <PublicLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/schedule-visit" element={<ScheduleVisitPage />} />
          <Route path="/showroom" element={<ShowroomPage />} />
          <Route path="/agreement" element={<AgreementPage />} />
          <Route path="/production" element={<ProductionPage />} />
          <Route path="/installation" element={<InstallationPage />} />
          <Route path="/client-dashboard" element={<ClientDashboardPage />} />
          <Route path="/follow-up" element={<FollowUpPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </PublicLayout>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
